import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { SystemText } from 'e4shared';
import posthog from 'posthog-js';
import './feedback.css';
import config from 'config';
import { IsSameDateIgnoreTimeAndTimeZone } from 'helpers/dates';

export const Feedback = () => {
    const { leftAsideOpen } = useSelector(state => state.ui);

    const [sentiment, setSentiment] = React.useState('');
    const [comment, setComment] = React.useState('');
    const [done, setDone] = React.useState(false);
    const [show, setShow] = React.useState(false);
    const [showSurvey, setShowSurvey] = React.useState(false);
    const user = useSelector(state => state.user.user);
    const outer = React.useRef();

    const st = (path, ...rest) => SystemText(`General/Feedback/${path}`, ...rest);

    const updateMetaDataToViewed = () => {
        if (user?.surveyMetaData?.displayedSurvey === false) {
            user.surveyMetaData.displayedSurvey = true;
        }
    };

    const load = () => {
        if (
            user?.surveyMetaData?.displayedSurvey === false &&
            IsSameDateIgnoreTimeAndTimeZone(
                user.surveyMetaData.nextSurveyDisplayDay,
                user.surveyMetaData.nextSurveyDisplayMonth,
                user.surveyMetaData.nextSurveyDisplayYear
            )
        ) {
            setShowSurvey(true);
        }
    };

    React.useEffect(() => {
        load();
    }, []);

    React.useEffect(() => {
        const handleClick = e => {
            if (!outer.current?.contains(e.target)) {
                setShow(false);
                setShowSurvey(false);
            }
        };

        document.addEventListener('mousedown', handleClick, false);

        return () => {
            document.removeEventListener('mousedown', handleClick, false);
        };
    }, []);

    React,
        useEffect(() => {
            if (showSurvey) {
                setShow(false);
                updateMetaDataToViewed();
                posthog.capture('survey shown', { $survey_id: config.posthog.survey });
            }
        }, [showSurvey]);

    const dismiss = clear => {
        setShowSurvey(false);
        if (clear) {
            setComment('');
            setSentiment('');
        } else {
            posthog.capture('survey dismissed', { $survey_id: config.posthog.survey });
        }
    };

    const submit = () => {
        setDone(true);
        posthog.capture('survey sent', {
            $survey_id: config.posthog.survey,
            $survey_response: sentiment,
            $survey_response_1: comment,
        });
    };

    return (
        <div className="aside-feedback" ref={outer}>
            <button
                className="btn btn-link"
                onClick={() => {
                    setShowSurvey(false);
                    setShow(true);
                }}
                aria-label="Show Feeedback"
            >
                <i className="fa fa-solid fa-circle-question" />
            </button>
            {show && (
                <div className={leftAsideOpen ? 'feedback-options-open' : 'feedback-options'}>
                    <div>
                        <button
                            className="btn btn-link"
                            onClick={() => {
                                setDone(false);
                                updateMetaDataToViewed();
                                setShowSurvey(true);
                            }}
                        >
                            {st('LeaveFeedback')}
                        </button>
                    </div>
                    <div className="mt10">
                        <button
                            className="btn btn-link"
                            onClick={() => {
                                window.open(config.supportUrl, '_blank');
                                setShow(false);
                            }}
                        >
                            {st('GetSupport')}
                        </button>
                    </div>
                </div>
            )}
            {showSurvey && (
                <div className={`aside-survey${leftAsideOpen ? '-open' : ''}`}>
                    {done ? (
                        <>
                            <div className="aside-survey-title">{st('Done/Title')}</div>
                            <div className="mt10 flex survey-btns">
                                <button className="btn btn-inverse flex-50" onClick={() => dismiss(true)}>
                                    {st('Done/Close')}
                                </button>
                            </div>
                        </>
                    ) : (
                        <>
                            <div className="aside-survey-title">{st('Title')}</div>
                            <div className="flex">
                                <button
                                    className={`btn btn-sentiment flex-33 ${sentiment === 'GREAT' ? 'selected' : ''}`}
                                    onClick={() => setSentiment('GREAT')}
                                >
                                    <i className="fa fa-face-smile-beam" />
                                    <span>{st('Sentiment/Great')}</span>
                                </button>
                                <button
                                    className={`btn btn-sentiment flex-33 ${sentiment === 'FINE' ? 'selected' : ''}`}
                                    onClick={() => setSentiment('FINE')}
                                >
                                    <i className="fa fa-face-diagonal-mouth" />
                                    <span>{st('Sentiment/Fine')}</span>
                                </button>
                                <button
                                    className={`btn btn-sentiment flex-33 ${sentiment === 'DONT_LOVE' ? 'selected' : ''}`}
                                    onClick={() => setSentiment('DONT_LOVE')}
                                >
                                    <i className="fa fa-face-worried" />
                                    <span>{st('Sentiment/DontLove')}</span>
                                </button>
                            </div>
                            <div>
                                <input
                                    type="text"
                                    placeholder={st('Placeholder')}
                                    maxLength={501}
                                    value={comment}
                                    onChange={e => setComment(e.target.value)}
                                    className="form-control"
                                />
                                <span className={`outof ${comment.length > 500 ? 'err' : ''}`}>{comment.length}/500</span>
                            </div>
                            <div className="mt20 flex survey-btns">
                                <button className="btn btn-inverse flex-50" onClick={() => dismiss()}>
                                    {st('Close')}
                                </button>
                                <button
                                    className="btn btn-primary flex-50"
                                    onClick={() => submit()}
                                    disabled={!sentiment || !comment || comment.length > 500}
                                >
                                    {st('Submit')}
                                </button>
                            </div>
                        </>
                    )}
                </div>
            )}
        </div>
    );
};

export default Feedback;
